import React from 'react';
import { ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';

const ONEDOC_URL = "https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid";

const ResistanceInsuline = () => {
  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title="Résistance à l'insuline — Nutritionniste Vevey, Clarens & Montreux | Nutri Centre Riviera"
        description="Accompagnement nutritionnel pour la résistance à l'insuline : perte de poids bloquée, fringales de sucre, fatigue chronique. Nutritionniste agréée ASCA & RME à Vevey, Clarens et Montreux. Riviera vaudoise."
        keywords="résistance insuline, résistance à l'insuline nutrition, insulino-résistance Vevey, fringales sucre, perte de poids résistance insuline, nutritionniste résistance insuline Clarens, SOPK résistance insuline Montreux, ASCA RME Riviera vaudoise"
        url="https://nutricentreriviera.ch/resistance-insuline-nutrition"
      />

      {/* Hero */}
      <section style={{ background: 'var(--gradient-hero)', padding: '5rem 0 4rem', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: '820px' }}>
          <span className="caption" style={{
            color: 'var(--accent-primary)',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            fontWeight: 700,
            fontSize: '0.85rem',
            display: 'inline-block',
            marginBottom: '1rem'
          }}>
            Résistance à l'insuline
          </span>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Votre corps ne répond plus comme avant ?
          </h1>
          <p className="heading-subtitle" style={{ maxWidth: '720px', margin: '0 auto 2.5rem' }}>
            Perte de poids bloquée, fringales de sucre, fatigue après les repas, prise de poids autour du ventre :
            ces signaux peuvent indiquer une <strong>résistance à l'insuline</strong>. Une cause hormonale silencieuse,
            mais réversible avec un accompagnement nutritionnel ciblé.
          </p>
          <a
            href={ONEDOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ textDecoration: 'none' }}
            data-testid="resistance-insuline-hero-cta"
          >
            Réserver une consultation
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Symptômes */}
      <section style={{ background: 'white', padding: '5rem 0' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Vous vous reconnaissez si…
            </h2>
            <p className="body-large" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
              La résistance à l'insuline touche près d'<strong>une femme sur trois</strong> après 35 ans —
              souvent sans diagnostic posé.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {[
              "Votre poids stagne malgré vos efforts alimentaires",
              "Vous avez des fringales de sucre, surtout en fin de journée",
              "Vous ressentez une fatigue importante après les repas",
              "Vous prenez du ventre alors que vous n'aviez pas ce profil avant",
              "Vous avez parfois des vertiges entre les repas",
              "Vous êtes diagnostiquée SOPK ou avez des cycles irréguliers"
            ].map((symptom, i) => (
              <div
                key={i}
                data-testid={`symptom-${i}`}
                style={{
                  display: 'flex',
                  gap: '0.85rem',
                  padding: '1.25rem 1.4rem',
                  background: 'var(--bg-page)',
                  borderRadius: '0.85rem',
                  border: '1px solid var(--border-light)',
                  alignItems: 'flex-start'
                }}
              >
                <AlertCircle size={22} strokeWidth={1.75} style={{
                  color: 'var(--accent-primary)',
                  flexShrink: 0,
                  marginTop: '2px'
                }} />
                <p className="body-medium" style={{ margin: 0, lineHeight: 1.55, color: 'var(--text-primary)' }}>
                  {symptom}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comprendre */}
      <section style={{ background: 'var(--bg-section)', padding: '5rem 0' }}>
        <div className="container" style={{ maxWidth: '780px' }}>
          <h2 className="heading-1" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            Comprendre la résistance à l'insuline
          </h2>
          <p className="body-large" style={{ lineHeight: 1.8, marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
            L'<strong>insuline</strong> est l'hormone qui régule votre taux de sucre dans le sang. Lorsque vos cellules
            deviennent moins sensibles à son signal, le pancréas en produit davantage pour compenser :
            c'est ce qu'on appelle la <strong>résistance à l'insuline</strong> (ou insulino-résistance).
          </p>
          <p className="body-large" style={{ lineHeight: 1.8, marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
            Le surplus d'insuline circulant favorise alors le <strong>stockage des graisses</strong>
            (surtout au niveau abdominal), provoque des <strong>fringales</strong>, perturbe l'équilibre hormonal
            féminin et peut, sans accompagnement, évoluer vers un pré-diabète puis un diabète de type 2.
          </p>
          <p className="body-large" style={{ lineHeight: 1.8, color: 'var(--text-primary)' }}>
            La bonne nouvelle : la résistance à l'insuline est <strong>réversible</strong> grâce à une approche
            nutritionnelle ciblée, sans privation, et adaptée à votre rythme de vie.
          </p>
        </div>
      </section>

      {/* Mon accompagnement */}
      <section style={{ background: 'white', padding: '5rem 0' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Mon accompagnement personnalisé
            </h2>
            <p className="body-large" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
              Un protocole nutritionnel sur mesure, validé scientifiquement et adapté à votre quotidien.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.25rem'
          }}>
            {[
              {
                title: "Bilan complet et analyse",
                text: "Anamnèse détaillée, étude des habitudes alimentaires et de votre composition corporelle pour identifier les leviers prioritaires."
              },
              {
                title: "Plan nutritionnel ciblé",
                text: "Une alimentation pensée pour stabiliser la glycémie, réduire la charge insulinique et apaiser les fringales — sans interdits arbitraires."
              },
              {
                title: "Suivi & ajustements",
                text: "Des consultations régulières pour ajuster le plan, célébrer vos progrès et installer durablement vos nouvelles habitudes."
              },
              {
                title: "Approche émotionnelle (optionnel)",
                text: "Si les compulsions ou le stress alimentaire sont présents, l'hypnopraxie complète idéalement le suivi nutritionnel."
              }
            ].map((step, i) => (
              <div
                key={i}
                data-testid={`acc-step-${i}`}
                style={{
                  background: 'var(--bg-page)',
                  borderRadius: '1rem',
                  border: '1px solid var(--border-light)',
                  padding: '1.75rem 1.5rem'
                }}
              >
                <CheckCircle2 size={26} strokeWidth={1.75} style={{
                  color: 'var(--accent-primary)',
                  marginBottom: '1rem'
                }} />
                <h3 className="heading-3" style={{ fontSize: '1.15rem', marginBottom: '0.65rem' }}>
                  {step.title}
                </h3>
                <p className="body-medium" style={{
                  color: 'var(--text-muted)',
                  lineHeight: 1.6,
                  margin: 0
                }}>
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section style={{
        background: 'var(--accent-primary)',
        color: 'white',
        padding: '5rem 0',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '720px' }}>
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            Prête à débloquer votre métabolisme ?
          </h2>
          <p className="body-large" style={{
            color: 'white',
            opacity: 0.95,
            lineHeight: 1.7,
            marginBottom: '2.5rem'
          }}>
            Un accompagnement nutritionnel ciblé peut transformer votre énergie, votre poids et votre rapport à l'alimentation.
          </p>
          <a
            href={ONEDOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              background: 'white',
              color: 'var(--accent-primary)',
              textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            data-testid="resistance-insuline-final-cta"
          >
            Réserver ma consultation
            <ArrowRight size={20} />
          </a>
          <p className="caption" style={{
            marginTop: '1rem',
            color: 'white',
            opacity: 0.9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <CheckCircle2 size={16} />
            Cabinets à Vevey &amp; Clarens (Montreux) — agréments ASCA &amp; RME
          </p>
        </div>
      </section>

      <RelatedPages items={relatedPagesMap['resistance-insuline-nutrition']} />
    </div>
  );
};

export default ResistanceInsuline;
